import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';

import { useNavigate, useLocation } from 'react-router-dom';

import {
  prepareAvatarPostImages,
  releaseAvatarPostImages,
} from '../studio/avatar/utils/prepareAvatarPostImages';

import { uploadToSpaces } from '../utils/uploadToSpaces';
import { compressImage } from '../utils/compressImage';
import { compressVideo } from '../utils/compressVideo';

import { useContext } from 'react';

import { usePostContext } from '../context/PostContext';
import { useProfileContext } from '../context/ProfileContext'; // ✅ Add this with other imports

import api from '../api';
import './CreatePostScreen.css';

import MentionDrawer from '../components/popups/MentionDrawer';
import { debounce } from 'lodash'; // if not yet installed: npm install lodash

import ActivateWalletPrompt from '../components/ActivateWalletPrompt';
import tipIcon from '../assets/tip-icon.png';
import PlanLimitDrawer from '../components/popups/PlanLimitDrawer';
import QuickTopUpPopup from '../components/popups/QuickTopUpPopup';
import receiptLogIcon from '../assets/receiptlog-icon.png';
import pollIcon from '../assets/poll-icon.png';
import placeholderProfile from '../assets/placeholder-profile.png';

import CreatePostAddOns from '../components/createpost/CreatePostAddOns';
import PostStyleSelector from '../components/createpost/PostStyleSelector';
import CreatePostBoostDrawer from '../components/createpost/CreatePostBoostDrawer';
import PostActionSelector from '../components/createpost/PostActionSelector';

import {
  getPostStyleCssVars,
  isPaidPostStyleTheme,
} from '../constants/postStyleThemes';

function CreatePostScreen() {
  const navigate = useNavigate();

const location = useLocation();

// Capture this entry's selection once.
const [incomingAvatarShare] = useState(
  () => location.state?.avatarShare || null
);

const [avatarPreparation, setAvatarPreparation] = useState(
  incomingAvatarShare ? 'pending' : 'idle'
);
const [avatarPreparationError, setAvatarPreparationError] =
  useState('');
const [avatarPreparationAttempt, setAvatarPreparationAttempt] =
  useState(0);

const preparedAvatarUrlsRef = useRef(new Set());
const avatarShareConsumedRef = useRef(false);

const avatarMediaLocked =
  Boolean(incomingAvatarShare) &&
  avatarPreparation !== 'ready';


  const [formData, setFormData] = useState({
    content: '',
    images: [],
    video: null,
    transactionId: '',
  });
  const [uploading, setUploading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [txnStatus, setTxnStatus] = useState(null); // 'valid', 'invalid', 'used', or null

const [showTxnInput, setShowTxnInput] = useState(false);

const [selectedBackgroundTheme, setSelectedBackgroundTheme] = useState('normal');
const [styleDrawerOpen, setStyleDrawerOpen] = useState(false);
const [fullscreenPreview, setFullscreenPreview] = useState(null);
const [isKeyboardOpen, setIsKeyboardOpen] = useState(false);

const createPostStyleVars = React.useMemo(
  () => getPostStyleCssVars(selectedBackgroundTheme),
  [selectedBackgroundTheme]
);


const [selectedStakeBoost, setSelectedStakeBoost] = useState(null);
const [boostDrawerOpen, setBoostDrawerOpen] = useState(false);

const [selectedPostAction, setSelectedPostAction] = useState(null);
const [postActionDrawerOpen, setPostActionDrawerOpen] = useState(false);



const { addPost } = usePostContext();


  const { addEnrichedPost } = useProfileContext(); // ✅ Use enriched version

  const storedUser = JSON.parse(localStorage.getItem('barterfi_user'));




const displayAvatar = storedUser?.profilePic || placeholderProfile;
const displayName = storedUser?.name || storedUser?.username || 'Pioneer';

const isMerchant =
  storedUser?.userType === 'merchant' &&
  (storedUser?.isMerchant || storedUser?.user?.isMerchant);

const isAdmin = storedUser?.isAdmin === true || !!storedUser?.adminRole;
const isApp = storedUser?.accountType === 'app';

const canUsePostAction =
  storedUser?.accountType === 'primary' &&
  storedUser?.userType === 'admin';

const displayRole = isAdmin
  ? 'Admin'
  : isApp
  ? 'App'
  : isMerchant
  ? 'Merchant'
  : 'Pioneer';


  const canSeePoll = true;
  const isSuspended = storedUser?.status === 'suspended';


const [mentionResults, setMentionResults] = useState([]);
const [showMentionDrawer, setShowMentionDrawer] = useState(false);
const [mentionQuery, setMentionQuery] = useState('');
const [activeMentionRange, setActiveMentionRange] = useState(null);

const [selectedMentions, setSelectedMentions] = useState([]);

const QUICK_BOOST_DEFAULT_HOURS = 24;

const quickBoostAmount = selectedStakeBoost
  ? Number(selectedStakeBoost.amountPi || 0)
  : 0;

  const [showTipsPill, setShowTipsPill] = useState(false);
  const [showWalletPrompt, setShowWalletPrompt] = useState(false);

const [showQuickTopUp, setShowQuickTopUp] = useState(false);
const quickTopUpTimerRef = useRef(null);


// 🧾 plan state
const [myPlan, setMyPlan] = useState(null);
const [planLoading, setPlanLoading] = useState(false);

// 🚫 limit drawer
const [planLimitOpen, setPlanLimitOpen] = useState(false);

const [limitMeta, setLimitMeta] = useState({
  feature: 'Post length',
  limit: 300,
  unit: 'characters',
});



const showError = (msg, ms = 3000) => {
  setErrorMsg(msg);
  window.clearTimeout(showError._t);
  showError._t = window.setTimeout(() => setErrorMsg(''), ms);
};


const openQuickTopUpAfterDelay = () => {
  if (quickTopUpTimerRef.current) {
    clearTimeout(quickTopUpTimerRef.current);
  }

  quickTopUpTimerRef.current = window.setTimeout(() => {
    setShowQuickTopUp(true);
  }, 2500);
};


const debouncedSearch = debounce(async (query) => {
  try {
    console.log('📡 Searching for mention:', query);

    const res = await api.get(
      `/search/mention?q=${encodeURIComponent(query || '')}`
    );

    console.log('✅ Results:', res.data);
    console.log('🧪 MentionDrawer Results:', JSON.stringify(res.data, null, 2));

    setMentionResults(Array.isArray(res.data) ? res.data : []);
  } catch (err) {
    console.error('❌ Mention search failed:', err);
    setMentionResults([]);
  }
}, 300);



const getActiveMentionAtCursor = (text = '', cursorPosition = 0) => {
  const safeCursor = Math.max(
    0,
    Math.min(Number(cursorPosition || 0), text.length)
  );

  const textBeforeCursor = text.slice(0, safeCursor);

  // Match only the @word directly before the cursor.
  // Works at start of text, after spaces, and after new lines.
  const match = textBeforeCursor.match(/(^|[\s\n])@([a-zA-Z0-9_.]*)$/);

  if (!match) return null;

  const query = match[2] || '';
  const start = safeCursor - query.length - 1;

  return {
    query,
    start,
    end: safeCursor,
  };
};

const closeMentionDrawer = () => {
  setShowMentionDrawer(false);
  setMentionQuery('');
  setMentionResults([]);
  setActiveMentionRange(null);
};

const updateMentionStateFromCursor = (text, cursorPosition) => {
  const activeMention = getActiveMentionAtCursor(text, cursorPosition);

  if (!activeMention) {
    closeMentionDrawer();
    return;
  }

  setActiveMentionRange({
    start: activeMention.start,
    end: activeMention.end,
  });

  setMentionQuery(activeMention.query);
  setShowMentionDrawer(true);
  debouncedSearch(activeMention.query);
};

// Read natural width/height from a File/Blob (e.g., your compressed image)
const readImageSizeFromBlob = (blob) =>
  new Promise((resolve, reject) => {
    try {
      const url = URL.createObjectURL(blob);
      const img = new Image();
      img.onload = () => {
        const out = { width: img.naturalWidth, height: img.naturalHeight };
        URL.revokeObjectURL(url);
        resolve(out);
      };
      img.onerror = (e) => {
        URL.revokeObjectURL(url);
        reject(e);
      };
      img.src = url;
    } catch (e) {
      reject(e);
    }
  });



// 🔢 progress state
const [phase, setPhase] = useState('idle'); // 'idle' | 'upload' | 'processing' | 'done' | 'error'
const [uploadPct, setUploadPct] = useState(0);
const [processingPct, setProcessingPct] = useState(0);
const [jobId, setJobId] = useState(null);
const pollRef = useRef(null);

// 👇 small helpers
const makeJobId = () =>
  (window.crypto?.randomUUID?.() || `jid_${Date.now()}_${Math.random().toString(36).slice(2)}`);

const makeCreatePostIdempotencyKey = () =>
  (window.crypto?.randomUUID?.() ||
    `create_post_${Date.now()}_${Math.random().toString(36).slice(2)}`);

const getCreatePostNetwork = () => {
  const host = window.location.hostname || '';

  if (
    host.includes('localhost') ||
    host.includes('127.0.0.1') ||
    host.includes('testnet.')
  ) {
    return 'testnet';
  }

  return 'mainnet';
};


const displayPct = phase === 'upload'
  ? uploadPct
  : phase === 'processing'
  ? processingPct
  : phase === 'done'
  ? 100
  : 0;

const startPolling = (jid) => {
  // clear any previous poller
  if (pollRef.current) clearInterval(pollRef.current);

  pollRef.current = setInterval(async () => {
    try {
      const r = await api.get(`/upload/progress/${jid}`, { timeout: 5000 });
      const { phase: serverPhase, percent } = r.data || {};

      if (serverPhase === 'processing') {
        if (phase !== 'processing') setPhase('processing');
        setProcessingPct(Math.round(Number(percent || 0)));
      }
      if (serverPhase === 'done') {
        setProcessingPct(100);
        setPhase('done');
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
      if (serverPhase === 'error') {
        setPhase('error');
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    } catch (_) {
      // ignore transient poll errors
    }
  }, 1000);
};


useEffect(() => {
  // cleanup on unmount
  return () => {
    if (pollRef.current) clearInterval(pollRef.current);

    if (quickTopUpTimerRef.current) {
      clearTimeout(quickTopUpTimerRef.current);
    }
  };
}, []);


useEffect(() => {
  if (formData.transactionId?.trim()) {
    setShowTxnInput(true);
  }
}, [formData.transactionId]);




const refreshMyPlan = async () => {
  try {
    console.log('🧾 [CreatePost] refreshMyPlan:start');
    setPlanLoading(true);

    const res = await api.get('/plans/me');

    console.log('🧾 [CreatePost] /plans/me status:', res.status);
    console.log('🧾 [CreatePost] /plans/me data:', res.data);
    console.log('🧾 [CreatePost] resolved plan:', res.data?.plan);

    setMyPlan(res.data?.plan || null);
  } catch (err) {
    console.log(
      '❌ [CreatePost] /plans/me failed:',
      err?.response?.status,
      err?.response?.data || err?.message
    );

 setMyPlan({ id: 'FREE', label: 'Free', limits: { postTextMax: 300, postImagesMax: 3, postVideoMaxMB: 20 } });

  } finally {
    console.log('🧾 [CreatePost] refreshMyPlan:done');
    setPlanLoading(false);
  }
};



useEffect(() => {
  let cancelled = false;

  const run = async () => {
    try {
      setPlanLoading(true);
      const res = await api.get('/plans/me');
      if (!cancelled) setMyPlan(res.data?.plan || null);
    } catch (err) {
      if (!cancelled)setMyPlan({ id: 'FREE', label: 'Free', limits: { postTextMax: 300, postImagesMax: 3, postVideoMaxMB: 20 } });

    } finally {
      if (!cancelled) setPlanLoading(false);
    }
  };

  run();
  return () => { cancelled = true; };
}, []);



useEffect(() => {
  const onFocus = () => refreshMyPlan();
  const onVis = () => {
    if (document.visibilityState === 'visible') refreshMyPlan();
  };

  window.addEventListener('focus', onFocus);
  document.addEventListener('visibilitychange', onVis);

  return () => {
    window.removeEventListener('focus', onFocus);
    document.removeEventListener('visibilitychange', onVis);
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, []);




const postLengthLimit = Number(myPlan?.limits?.postTextMax ?? 300);
const postImagesLimit = Number(myPlan?.limits?.postImagesMax ?? 3);


useEffect(() => {
  if (
    !incomingAvatarShare ||
    avatarShareConsumedRef.current ||
    !myPlan ||
    planLoading
  ) {
    return;
  }

  const controller = new AbortController();
  let cancelled = false;

  async function prepareIncomingAvatars() {
    setAvatarPreparation('preparing');
    setAvatarPreparationError('');

    try {
      const creations = incomingAvatarShare.creations;

      if (!Array.isArray(creations) || creations.length === 0) {
        throw new Error('No avatars were supplied. Return to Studio.');
      }

      if (
        !Number.isSafeInteger(postImagesLimit) ||
        postImagesLimit < 0
      ) {
        throw new Error('Could not read your image allowance.');
      }

      if (creations.length > postImagesLimit) {
        throw new Error(
          `Your plan allows ${postImagesLimit} images per post. ` +
          'Return to Studio and select fewer avatars.'
        );
      }

      const images = await prepareAvatarPostImages(creations, {
        signal: controller.signal,
      });

      if (cancelled) {
        releaseAvatarPostImages(images);
        return;
      }

      for (const image of images) {
        preparedAvatarUrlsRef.current.add(image.url);
      }

      avatarShareConsumedRef.current = true;

      setFormData((current) => ({
        ...current,
        images,
        video: null,
      }));

      setAvatarPreparation('ready');

      // Remove only the consumed share payload from this history entry.
      const nextState = { ...(location.state || {}) };
      delete nextState.avatarShare;

      navigate(
        {
          pathname: location.pathname,
          search: location.search,
          hash: location.hash,
        },
        {
          replace: true,
          state: Object.keys(nextState).length ? nextState : null,
        }
      );
    } catch (error) {
      if (cancelled || error?.name === 'AbortError') return;

      setAvatarPreparation('error');
      setAvatarPreparationError(
        error?.message || 'Could not prepare your avatars.'
      );
    }
  }

  prepareIncomingAvatars();

  return () => {
    cancelled = true;
    controller.abort();
  };
}, [
  incomingAvatarShare,
  myPlan,
  planLoading,
  postImagesLimit,
  avatarPreparationAttempt,
  location.pathname,
  location.search,
  location.hash,
  location.state,
  navigate,
]);



// Release Studio preview URLs when attachments are removed
// or replaced by a video.
useEffect(() => {
  const activeUrls = new Set(
    formData.images.map((image) => image.url)
  );

  for (const url of preparedAvatarUrlsRef.current) {
    if (!activeUrls.has(url)) {
      URL.revokeObjectURL(url);
      preparedAvatarUrlsRef.current.delete(url);
    }
  }
}, [formData.images]);

// Release any remaining Studio previews when leaving the composer.
useEffect(() => {
  const ownedUrls = preparedAvatarUrlsRef.current;

  return () => {
    for (const url of ownedUrls) {
      URL.revokeObjectURL(url);
    }

    ownedUrls.clear();
  };
}, []);


// 🎥 Feature 3: plan-based video size (MB)
const fallbackVideoMaxMBByPlan = {
  FREE: 20,
  BASIC: 35,
  PREMIUM: 50,
  PREMIUM_PLUS: 100,
};

const postVideoMaxMB = Number(
  myPlan?.limits?.postVideoMaxMB ??
    fallbackVideoMaxMBByPlan[myPlan?.id || 'FREE'] ??
    20
);

const postVideoMaxBytes = postVideoMaxMB * 1024 * 1024;


console.log('🧾 [CreatePost] myPlan state:', myPlan);
console.log('🧾 [CreatePost] myPlan.limits raw:', myPlan?.limits);
console.log('🧾 [CreatePost] myPlan.limits JSON:', JSON.stringify(myPlan?.limits, null, 2));
console.log('🧾 [CreatePost] postLengthLimit:', postLengthLimit);

const isPostTooLong = (text) => (text || '').length > postLengthLimit;

const contentLen = (formData.content || '').length;
const remainingChars = postLengthLimit - contentLen;

console.log('🧾 [CreatePost] contentLen:', contentLen);
console.log('🧾 [CreatePost] remainingChars:', remainingChars);



  useEffect(() => {
    const username = storedUser?.username;
    if (!username) return;

    const host = window.location.hostname || '';
    const isTestnetHost = host.includes('testnet.');
    const isMainnetHost = !isTestnetHost && host.includes('barterfi.app');

    // Only run this on mainnet host
    if (!isMainnetHost) return;

    const checkWalletActivation = async () => {
      try {
        const res = await api.get(`/wallet/${username}?network=mainnet`);
        const history = res.data?.history || [];

        const depositTotal = history
          .filter((tx) => ['deposit', 'topup'].includes(tx.type))
          .reduce((sum, tx) => sum + Number(tx.amount || 0), 0);

        const threshold = Number(
          process.env.REACT_APP_MIN_MAINNET_ACTIVATION_PI || 0.1
        );

        const inactive = depositTotal < threshold;

        // Only show the pill if wallet is inactive
        setShowTipsPill(inactive);
      } catch (err) {
        console.warn('Wallet activation check failed:', err);
      }
    };

    checkWalletActivation();
  }, [storedUser?.username]);




  const handleTipsPillClick = () => {
    setShowWalletPrompt(true);
  };

  const handleCloseWalletPrompt = () => {
    // Just close the popup, no snooze
    setShowWalletPrompt(false);
  };

  const handleActivateWallet = () => {
    setShowWalletPrompt(false);
    navigate('/my-wallet');
  };


const handleOpenPoll = () => {
  if (avatarMediaLocked || uploading) return;
  navigate('/create-poll');
};


const handleMentionSelect = (mention) => {
  const value = formData.content || '';
  const textarea = textareaRef.current;

  const fallbackCursor =
    textarea?.selectionStart ?? textarea?.selectionEnd ?? value.length;

  const range =
    activeMentionRange || getActiveMentionAtCursor(value, fallbackCursor);

  if (!range) {
    closeMentionDrawer();
    return;
  }

  const rawInsertText = `@${mention.username}`;
  const before = value.slice(0, range.start);
  const after = value.slice(range.end);

  // Add a space only when the mention is touching normal text after it.
  const shouldAddSpaceAfter =
    after.length > 0 && !/^[\s\n.,!?;:)]/.test(after);

  const insertText = shouldAddSpaceAfter
    ? `${rawInsertText} `
    : rawInsertText;

  const newText = `${before}${insertText}${after}`;
  const nextCursorPosition = before.length + insertText.length;

  setFormData((prev) => ({
    ...prev,
    content: newText,
  }));

  closeMentionDrawer();

  // ✅ Accumulate selected mentions without duplicates
  setSelectedMentions((prev) => {
    const exists = prev.some(
      (m) => m.username?.toLowerCase() === mention.username?.toLowerCase()
    );

    return exists ? prev : [...prev, mention];
  });

  requestAnimationFrame(() => {
    const el = textareaRef.current;
    if (!el) return;

    el.focus();
    el.setSelectionRange(nextCursorPosition, nextCursorPosition);


  });
};



// 🔽 Auto-resize setup for the "What's happening?" textarea
const textareaRef = useRef(null);
const keyboardBaselineRef = useRef(0);

useEffect(() => {
  const viewport = window.visualViewport;

  if (!viewport) return undefined;

  keyboardBaselineRef.current = Math.max(
    window.innerHeight,
    viewport.height
  );

  const handleViewportResize = () => {
    const currentHeight = viewport.height;
    const baselineHeight = keyboardBaselineRef.current;

    const textareaIsFocused =
      document.activeElement === textareaRef.current;

    const keyboardHeight =
      baselineHeight - currentHeight;

    const keyboardVisible =
      textareaIsFocused && keyboardHeight > 140;

    setIsKeyboardOpen(keyboardVisible);

    if (!textareaIsFocused && currentHeight > baselineHeight) {
      keyboardBaselineRef.current = currentHeight;
    }
  };

  viewport.addEventListener('resize', handleViewportResize);

  return () => {
    viewport.removeEventListener('resize', handleViewportResize);
  };
}, []);


useLayoutEffect(() => {
  const el = textareaRef.current;
  const hasMedia =
    formData.images.length > 0 || Boolean(formData.video);

  if (!el) return;

  if (!hasMedia) {
    el.style.removeProperty('height');
    return;
  }

  el.style.height = 'auto';

  const minimumHeight = 72;
  const maximumHeight = 180;

  const nextHeight = Math.min(
    Math.max(el.scrollHeight, minimumHeight),
    maximumHeight
  );

  el.style.height = `${nextHeight}px`;
}, [
  formData.content,
  formData.images.length,
  formData.video,
]);



const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

const handleQuickBoostChange = (direction) => {
  setSelectedStakeBoost((prev) => {
    const currentAmount = Number(prev?.amountPi || 0);
    const nextAmount = clamp(currentAmount + direction, 0, 100);

    if (nextAmount <= 0) {
      return null;
    }

    return {
      amountPi: nextAmount,
      durationHours: Number(prev?.durationHours || QUICK_BOOST_DEFAULT_HOURS),
    };
  });
};

const handleImageChange = (e) => {
  const files = Array.from(e.target.files || []);

if (avatarMediaLocked || uploading) {
  e.target.value = '';
  return;
}

  if (!files.length) return;

  const max = postImagesLimit;
  const remaining = Math.max(0, max - formData.images.length);

  // Already at limit
  if (remaining <= 0) {

 showError(`Image limit reached. Max ${max} images on ${myPlan?.label || 'Free'}.`);

    setLimitMeta({ feature: 'Images per post', limit: max, unit: 'images' });
    setPlanLimitOpen(true);
    e.target.value = ''; // allow re-select same file later
    return;
  }

  // Take only what we can fit
  const accepted = files.slice(0, remaining);
  const rejectedCount = files.length - accepted.length;

  const imageArray = accepted.map((file) => ({
    file,
    url: URL.createObjectURL(file),
  }));

  setFormData((prev) => ({
    ...prev,
    images: [...prev.images, ...imageArray],
    video: null, // keep your current rule: images clear video
  }));

  // If user tried to add too many, show upgrade drawer
  if (rejectedCount > 0) {

showError(`Only ${max} images allowed on ${myPlan?.label || 'Free'}.`);

    setLimitMeta({ feature: 'Images per post', limit: max, unit: 'images' });
    setPlanLimitOpen(true);
  }

  e.target.value = '';
};




const handleVideoChange = (e) => {
  const file = e.target.files?.[0];


if (avatarMediaLocked || uploading) {
  e.target.value = '';
  return;
} 


 if (!file) return;

  const allowed = ['video/mp4', 'video/webm', 'video/quicktime'];
  if (!allowed.includes(file.type)) {
    showError('Unsupported video format. Use MP4/WebM/MOV.');
    e.target.value = '';
    return;
  }

  // ✅ Feature 3: plan-based max size
  if (file.size > postVideoMaxBytes) {
    showError(`Video too large. Max ${postVideoMaxMB}MB on ${myPlan?.label || 'Free'}.`);

    setLimitMeta({ feature: 'Video size', limit: postVideoMaxMB, unit: 'MB' });
    setPlanLimitOpen(true);

    e.target.value = '';
    return;
  }

  setFormData({
    ...formData,
    video: { file, url: URL.createObjectURL(file) },
    images: [], // keep your rule: video clears images
  });

  e.target.value = '';
};


  const validateTransactionId = async (id) => {
    if (!id.trim()) {
      setTxnStatus(null);
      return;
    }

    try {
      const storedUser = JSON.parse(localStorage.getItem('barterfi_user'));
      const { username } = storedUser;

      const res = await api.post('/posts/validate-transaction', { transactionId: id, username });

      if (res.data.valid) {
        setTxnStatus('valid');
      } else if (res.data.used) {
        setTxnStatus('used');
      } else {
        setTxnStatus('invalid');
      }
    } catch (err) {
      console.error('❌ Transaction validation failed:', err);
      setTxnStatus('invalid');
    }
  };





  const handleRemoveImage = (i) => {
    const updated = [...formData.images];
    updated.splice(i, 1);
    setFormData({ ...formData, images: updated });
  };



const handleSavePostAction = (nextAction) => {
  if (
    !nextAction?.destinationKey ||
    !nextAction?.label?.trim()
  ) {
    return;
  }

  setSelectedPostAction({
    destinationKey: nextAction.destinationKey,
    label: nextAction.label.trim(),
  });

  setPostActionDrawerOpen(false);
};

const handleClearPostAction = () => {
  setSelectedPostAction(null);
};



const handleSubmit = async () => {

if (uploading) return;

if (avatarMediaLocked) {
  showError(
    avatarPreparation === 'error'
      ? 'Retry preparing your avatars before posting.'
      : 'Please wait while your avatars are prepared.'
  );
  return;
}


  // 🚫 Hard block if post is too long (open drawer ONLY here)
  if (isPostTooLong(formData.content)) {
    showError(
      `Post too long. Max ${postLengthLimit} characters on ${myPlan?.label || 'Free'}.`
    );
 setLimitMeta({ feature: 'Post length', limit: postLengthLimit, unit: 'characters' });

    setPlanLimitOpen(true);
    return;
  }

// 🚫 Hard block if too many images (Feature 2)
if (formData.images.length > postImagesLimit) {
  showError(`Too many images. Max ${postImagesLimit} on ${myPlan?.label || 'Free'}.`);
  setLimitMeta({ feature: 'Images per post', limit: postImagesLimit, unit: 'images' });
  setPlanLimitOpen(true);
  return;
}

// 🚫 Hard block if video too large (Feature 3)
if (formData.video?.file && formData.video.file.size > postVideoMaxBytes) {
  showError(`Video too large. Max ${postVideoMaxMB}MB on ${myPlan?.label || 'Free'}.`);
  setLimitMeta({ feature: 'Video size', limit: postVideoMaxMB, unit: 'MB' });
  setPlanLimitOpen(true);
  return;
}


  const hasText = formData.content.trim().length > 0;
  const hasImages = formData.images.length > 0;
  const hasVideo = !!formData.video;
  const hasTxn = formData.transactionId.trim().length > 0;

  if (!hasText && !hasImages && !hasVideo) {
    showError('Add something to post');
    return;
  }

  if (hasTxn && txnStatus !== 'valid') {
    showError('Invalid or used transaction ID');
    return;
  }

  if (hasTxn && !hasImages && !hasVideo) {
    showError('Attach an image or video to verify transaction');
    return;
  }

  setUploading(true);

let success = false;
let publishedPostId = null;
let publishedBy = null;

  try {


    const uploadedImages = hasImages
      ? await Promise.all(
          formData.images.map(async (img) => {
            // 1) Compress
            const compressedFile = await compressImage(img.file);

            // 2) Measure dimensions of compressed image
            const { width, height } =
              await readImageSizeFromBlob(compressedFile);

            // 3) Upload
            const url = await uploadToSpaces(
              compressedFile,
              'images'
            );

            // 4) Preserve the supported Studio marker.
            return {
              url,
              width,
              height,
              ...(img.source === 'studio_avatar'
                ? { source: 'studio_avatar' }
                : {}),
            };
          })
        )
      : [];


    let uploadedVideo = null;
    let thumbnailUrl = null;

    if (hasVideo) {
      const jid = makeJobId();
      setJobId(jid);
      setPhase('upload');
      setUploadPct(0);
      setProcessingPct(0);

      startPolling(jid);

      const fd = new FormData();
      fd.append('file', formData.video.file, formData.video.file.name || 'video.mp4');
      fd.append('jobId', jid);

      const up = await api.post('/upload/upload-post-video', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (e) => {
          if (!e.total) return;
          const pct = Math.round((e.loaded / e.total) * 100);
          setUploadPct(pct);
          // do NOT flip to processing here; polling controls phase
        },
        maxBodyLength: Infinity,
        maxContentLength: Infinity,
        timeout: 600000,
      });

      uploadedVideo = up.data.videoUrl;
      thumbnailUrl = up.data.thumbnailUrl;

      setPhase('done');
      setProcessingPct(100);
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    }

    const storedUser = JSON.parse(localStorage.getItem('barterfi_user'));
    const refreshed = await api.get(`/users/${storedUser.username}`);
    const user = refreshed.data;

    localStorage.setItem('barterfi_user', JSON.stringify(user));

    // ✅ Extract all @mentions from content
    const matchedHandles = formData.content.match(/@[a-zA-Z0-9_]+/g) || [];
    const mentions = [];
    const mentionMap = {};

    matchedHandles.forEach((handle) => {
      const username = handle.slice(1).toLowerCase();
      const match = selectedMentions.find(
        (item) => item.username.toLowerCase() === username
      );

      if (match) {
        if (!mentions.some((m) => m.username === match.username)) {
          mentions.push({
            username: match.username,
            name: match.name,
            type: match.type,
          });

          mentionMap[`@${match.username}`] = {
            username: match.username,
            type: match.type,
            name: match.name,
          };
        }
      }
    });


const selectedThemeKey = selectedBackgroundTheme || 'normal';
const paidThemeSelected = isPaidPostStyleTheme(selectedThemeKey);

const stakeBoostSelected =
  selectedStakeBoost &&
  Number(selectedStakeBoost.amountPi) >= 1 &&
  Number(selectedStakeBoost.durationHours) > 0;

const normalizedStakeBoost = stakeBoostSelected
  ? {
      amountPi: Number(selectedStakeBoost.amountPi),
      durationHours: Number(selectedStakeBoost.durationHours),
    }
  : null;

const hasPaidCreatePostAddOn = paidThemeSelected || stakeBoostSelected;

const post = {

  name: user.name,
  username: user.username,
  profilePic: user.profilePic,
  content: formData.content,

  media: hasVideo ? [uploadedVideo] : uploadedImages, // images = [{url,width,height}]
  mediaType: hasVideo ? 'video' : hasImages ? 'image' : '',

  transactionId: hasTxn ? formData.transactionId.trim() : null,
  thumbnail: hasVideo ? thumbnailUrl : null,
  mentions, // backend-only
  mentionMap, // frontend use too

  // ✅ Create Post add-ons
  // Frontend sends only the add-on key.
  // Backend calculates the real price.


addOns: {
  backgroundTheme: selectedThemeKey,
  stakeBoost: normalizedStakeBoost,
},

// ✅ Post Action is separate from paid add-ons
...(selectedPostAction
  ? {
      postAction: {
        enabled: true,
        destinationKey: selectedPostAction.destinationKey,
        label: selectedPostAction.label,
      },
    }
  : {}),

// ✅ Required only when a paid style or stake boost is selected
...(hasPaidCreatePostAddOn
  ? {
      network: getCreatePostNetwork(),
      idempotencyKey: makeCreatePostIdempotencyKey(),
    }
  : {}),

};

    const res = await api.post('/posts', post);
    const postId = res.data._id;


    const resolvedIsAdmin = user?.isAdmin === true || !!user?.adminRole;
    const resolvedAdminRole = user?.adminRole || null;
    const resolvedAccountType = user?.accountType || 'primary';
    const resolvedIsApp =
      user?.isApp === true || resolvedAccountType === 'app';

    let enriched = {
      ...res.data,
      id: postId,
      type: 'post',

      // ✅ keep top-level identity fields in sync for all screens
      name: user.name,
      username: user.username,
      profilePic: user.profilePic,
      location: user.location,

      userType: user.userType,
      isMerchant: user.isMerchant,
      merchantActive: user.isMerchant === true,
      isAdmin: resolvedIsAdmin,
      adminRole: resolvedAdminRole,
      accountType: resolvedAccountType,
      isApp: resolvedIsApp,


      user: {
        name: user.name,
        username: user.username,
        profilePic: user.profilePic,
        location: user.location,


        userType: user.userType,
        isMerchant: user.isMerchant,
        merchantActive: user.isMerchant === true,
        isAdmin: resolvedIsAdmin,
        adminRole: resolvedAdminRole,
        accountType: resolvedAccountType,
        isApp: resolvedIsApp,

        badges: Array.isArray(user.badges) ? user.badges : [],
        badgesBrief: Array.isArray(user.badges)
          ? user.badges.map((b) => b?.type).filter(Boolean)
          : [],
      },

      timestamp: res.data.createdAt,
      mentionMap,
    };


    try {
      const interaction = await api.get(`/posts/interactions/${postId}`);
      enriched.likes = interaction.data.likes || [];
      enriched.comments = interaction.data.comments || [];
      enriched.tips = interaction.data.tips || [];
      enriched.savedBy = interaction.data.savedBy || [];

      enriched.likesCount = enriched.likes.length;
      enriched.commentsCount = enriched.comments.length;
      enriched.tipsCount = enriched.tips.length;
      enriched.userHasLiked = enriched.likes.some((u) => u.username === user.username);
      enriched.userHasSaved = enriched.savedBy.includes(user.username);
    } catch (err) {
      enriched.likes = [];
      enriched.comments = [];
      enriched.tips = [];
      enriched.savedBy = [];

      enriched.likesCount = 0;
      enriched.commentsCount = 0;
      enriched.tipsCount = 0;
      enriched.userHasLiked = false;
      enriched.userHasSaved = false;
    }

addPost(enriched);
await addEnrichedPost(enriched, user.username);

publishedPostId = postId;
publishedBy = user.username;
success = true;

} catch (err) {
  console.error('❌ Full error during post:', err);

  const code = err?.response?.data?.code;
  const status = err?.response?.status;
  const backendMsg = err?.response?.data?.error || err?.response?.data?.message;

const lowerBackendMsg = String(backendMsg || '').toLowerCase();

if (
  code === 'INSUFFICIENT_PI' ||
  code === 'WALLET_NOT_FOUND' ||
  code === 'INSUFFICIENT_BALANCE' ||
  code === 'WALLET_INSUFFICIENT_BALANCE' ||
  err?.response?.data?.action === 'TOP_UP_REQUIRED' ||
  lowerBackendMsg.includes('insufficient') ||
  lowerBackendMsg.includes('not enough pi') ||
  lowerBackendMsg.includes('no active barterfi wallet') ||
  lowerBackendMsg.includes('wallet not found') ||
  lowerBackendMsg.includes('balance')
) {
  showError('Insufficient Pi', 2400);
  openQuickTopUpAfterDelay();
  return;
}

if (
  code === 'INVALID_ADDON' ||
  code === 'INVALID_POST_ADDON' ||
  lowerBackendMsg.includes('add-on') ||
  lowerBackendMsg.includes('addon')
) {
  showError(backendMsg || 'This post style is not available.');
  return;
}

  // ✅ Feature 3: plan-based video limit errors
  if (code === 'POST_VIDEO_SIZE_LIMIT_REACHED') {
    const limit = Number(err?.response?.data?.limit || postVideoMaxMB);
    showError(`Video too large. Max ${limit}MB on ${myPlan?.label || 'Free'}.`);
    setLimitMeta({ feature: 'Video size', limit, unit: 'MB' });
    setPlanLimitOpen(true);
    return;
  }

  // Multer file size limit often ends up as 413 or message text
  if (status === 413 || String(backendMsg || '').toLowerCase().includes('file too large')) {
    showError(`Video too large. Max ${postVideoMaxMB}MB on ${myPlan?.label || 'Free'}.`);
    setLimitMeta({ feature: 'Video size', limit: postVideoMaxMB, unit: 'MB' });
    setPlanLimitOpen(true);
    return;
  }

  // Existing fall-through
  showError('Post failed. Try again.');


} finally {
  setUploading(false);

  if (success) {
    navigate('/home', {

state: {
  publishedPostId,
  publishedBy,
},
    });
  }
}
};




  if (isSuspended) {
    return (
      <div className="create-post-screen">

<header className="create-post-header create-post-header--simple">
  Create post
</header>

        <div className="create-post-suspended-banner">
          🚫 Your account has been suspended. You cannot post until BarterFi reinstates your access.
          <br />
          <button
            className="appeal-button"
            onClick={() => navigate('/appeal')}
          >
            Appeal Suspension
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="create-post-screen">

{uploading && (
  <div className="create-post-overlay">
    <img
      src={require('../assets/createpost.gif')}
      alt="Posting..."
      className="post-spinner-large"
    />
    <p className="posting-text">
      {phase === 'processing' || phase === 'done'
        ? `Posting ${processingPct}%`
        : 'Posting...'}
    </p>
  </div>
)}



<header className="create-post-header">
  <button
    type="button"
    className="create-post-header-close"
    onClick={() => navigate(-1)}
    aria-label="Close create post"
  >
    ×
  </button>

  <h1 className="create-post-header-title">Create post</h1>

  <button
    type="button"
    className="create-post-header-submit"
    onClick={handleSubmit}

  disabled={uploading || avatarMediaLocked}
  >
    {uploading ? 'Posting...' : 'Post'}
  </button>
</header>



<div
  className={`create-post-body ${
    isKeyboardOpen ? 'is-keyboard-open' : ''
  }`}
>


        {incomingAvatarShare && avatarPreparation !== 'ready' && (
          <div
            className="createpostscreen-avatar-preparation"
            aria-busy={avatarPreparation !== 'error'}
          >
            {avatarPreparation === 'error' ? (
              <>
                <p role="alert">{avatarPreparationError}</p>

                <div className="createpostscreen-avatar-preparation__actions">
                  <button
                    type="button"
                    onClick={() => {
                      setAvatarPreparationError('');
                      setAvatarPreparation('pending');
                      setAvatarPreparationAttempt(
                        (value) => value + 1
                      );
                    }}
                  >
                    Retry
                  </button>

                  <button
                    type="button"
                    onClick={() => navigate(-1)}
                  >
                    Back to Studio
                  </button>
                </div>
              </>
            ) : (
              <p role="status">
                {avatarPreparation === 'pending'
                  ? 'Checking your image allowance…'
                  : 'Preparing images…'}
              </p>
            )}
          </div>
        )}

        {errorMsg && (
          <div className="post-error-banner">{errorMsg}</div>
        )}


<div className="createpostscreen-author">
  <div className="createpostscreen-author-avatar-wrap">
    <img
      src={displayAvatar}
      alt={displayName}
      className="createpostscreen-author-avatar"
    />
  </div>

  <div className="createpostscreen-author-meta">
    <div className="createpostscreen-author-name">{displayName}</div>

<div
  className={`createpostscreen-role-pill ${
    displayRole === 'Pioneer' ? 'is-pioneer' : ''
  }`}
>
  {displayRole}
</div>

  </div>


</div>



<CreatePostAddOns
  selectedTheme={selectedBackgroundTheme}
  selectedStakeBoost={selectedStakeBoost}
  selectedPostAction={selectedPostAction}
  canUsePostAction={canUsePostAction}
  onOpenStyleDrawer={() => setStyleDrawerOpen(true)}
  onOpenBoostDrawer={() => setBoostDrawerOpen(true)}
  onOpenPlans={() => navigate('/plans')}
  onOpenActionDrawer={() => setPostActionDrawerOpen(true)}
/>




<div
  className={`post-textarea-wrap createpostscreen-style-${selectedBackgroundTheme} ${
    formData.images.length > 0 || formData.video ? 'has-media' : ''
  }`}
  style={createPostStyleVars}
>

<textarea
  ref={textareaRef}
  className="whats-happening createpostscreen-themed-textarea"

  placeholder="What's happening?"
  value={formData.content}
onFocus={() => setIsKeyboardOpen(true)}
onBlur={() => setIsKeyboardOpen(false)}

    onChange={(e) => {

      const newContent = e.target.value;
      setFormData({ ...formData, content: newContent });

if (newContent.length > postLengthLimit) {
  showError(`Post too long. Max ${postLengthLimit} characters on ${myPlan?.label || 'Free'}.`);
} else {
  setErrorMsg((prev) => (prev?.includes('Post too long') ? '' : prev));
}



const cursorPosition = e.target.selectionStart ?? newContent.length;
updateMentionStateFromCursor(newContent, cursorPosition);

    }}
  />


{(formData.images.length > 0 || formData.video) && (
  <div className="createpostscreen-preview-area">
    {formData.images.map((img, i) => (
      <div key={i} className="preview-box">

<img
  src={img.url}
  alt="Preview"
  className="preview-img"
  onClick={() =>
    setFullscreenPreview({
      type: 'image',
      url: img.url,
    })
  }
/>

        <img
          src={require('../assets/close-icon.png')}
          alt="Remove"
          className="preview-remove-icon"
          onClick={() => handleRemoveImage(i)}
        />
      </div>
    ))}

    {formData.images.length > 0 &&
      formData.images.length < postImagesLimit && (
        <div
          className="preview-box add-preview-box"
          onClick={() =>
            document.getElementById('addMoreInput').click()
          }
        >
          <img
            src={require('../assets/add-icon.png')}
            alt="Add"
            className="add-preview-icon"
          />
        </div>
      )}

    {formData.video && (
      <div className="video-preview-box">

<video
  src={formData.video.url}
  className="video-preview"
  muted
  playsInline
  onClick={() =>
    setFullscreenPreview({
      type: 'video',
      url: formData.video.url,
    })
  }
/>

        <img
          src={require('../assets/close-icon.png')}
          alt="Remove Video"
          className="video-remove-icon"
          onClick={() =>
            setFormData({
              ...formData,
              video: null,
            })
          }
        />
      </div>
    )}

  </div>
)}

</div>

  <div className="createpostscreen-text-meta-row">

    <div className="createpostscreen-quick-boost" aria-label="Quick boost amount">
      <button
        type="button"
        className="createpostscreen-quick-boost-btn"
        onClick={() => handleQuickBoostChange(-1)}
        disabled={quickBoostAmount <= 0}
        aria-label="Decrease boost"
      >
        −
      </button>

      <button
        type="button"
        className={`createpostscreen-quick-boost-amount ${
          quickBoostAmount > 0 ? 'is-active' : ''
        }`}
        onClick={() => setBoostDrawerOpen(true)}
        aria-label="Open boost settings"
      >
        {quickBoostAmount}
      </button>

      <button
        type="button"
        className="createpostscreen-quick-boost-btn"
        onClick={() => handleQuickBoostChange(1)}
        aria-label="Increase boost"
      >
        +
      </button>

      <span className="createpostscreen-quick-boost-label">Pi boost</span>
    </div>

    <div
      className={`post-char-count ${
        remainingChars <= 50 ? 'warn' : ''
      } ${remainingChars < 0 ? 'over' : ''}`}
    >
      {remainingChars}
    </div>


</div>






        <input
          type="file"
          id="addMoreInput"
          multiple
          accept="image/*"
          onChange={handleImageChange}
          disabled={uploading || avatarMediaLocked}
          hidden
        />


        {showTxnInput && (
          <div className="createpostscreen-txn-wrap">
            <input
              type="text"
              className="createpostscreen-txn-input"
              placeholder="TXN ID (optional)"
              value={formData.transactionId}
              onChange={(e) => {
                const val = e.target.value;
                setFormData({ ...formData, transactionId: val });
                validateTransactionId(val);
              }}
            />

            {txnStatus === 'valid' && (
              <p className="txn-feedback valid">✅ Transaction verified</p>
            )}
            {txnStatus === 'used' && (
              <p className="txn-feedback used">⚠️ Already used</p>
            )}
            {txnStatus === 'invalid' && (
              <p className="txn-feedback invalid">❌ Invalid ID</p>
            )}
          </div>
        )}



<div className="createpostscreen-action-row">
  {showTipsPill && (
    <button
      type="button"
      className="createpostscreen-action-item"
      onClick={handleTipsPillClick}
      aria-label="Tips"
    >
      <span className="createpostscreen-action-btn">
        <img
          src={tipIcon}
          alt="Tips"
          className="createpostscreen-action-icon"
        />
      </span>
      <span className="createpostscreen-action-label">Tips</span>
    </button>
  )}

  {canSeePoll && (


    <button
      type="button"
      className="createpostscreen-action-item"
      onClick={handleOpenPoll}
      aria-label="Poll"
      disabled={uploading || avatarMediaLocked}
    >

      <span className="createpostscreen-action-btn">
        <img
          src={pollIcon}
          alt="Poll"
          className="createpostscreen-action-icon"
        />
      </span>
      <span className="createpostscreen-action-label">Poll</span>
    </button>
  )}

  <label className="createpostscreen-action-item" aria-label="Add Image">
    <span className="createpostscreen-action-btn">
      <img
        src={require('../assets/photo-icon.png')}
        alt="Add Image"
        className="createpostscreen-action-icon"
      />
    </span>
    <span className="createpostscreen-action-label">Image</span>


    <input
      type="file"
      multiple
      accept="image/*"
      onChange={handleImageChange}
      disabled={uploading || avatarMediaLocked}
      hidden
    />


  </label>

  <label className="createpostscreen-action-item" aria-label="Add Video">
    <span className="createpostscreen-action-btn">
      <img
        src={require('../assets/video-icon.png')}
        alt="Add Video"
        className="createpostscreen-action-icon"
      />
    </span>
    <span className="createpostscreen-action-label">Video</span>



    <input
      type="file"
      accept="video/*"
      onChange={handleVideoChange}
      disabled={uploading || avatarMediaLocked}
      hidden
    />

  </label>

  <button
    type="button"
    className="createpostscreen-action-item"
    onClick={() => setShowTxnInput((prev) => !prev)}
    aria-label="TNX"
  >
    <span className={`createpostscreen-action-btn ${showTxnInput ? 'is-active' : ''}`}>
      <img
        src={receiptLogIcon}
        alt="TNX"
        className="createpostscreen-action-icon"
      />
    </span>
    <span className="createpostscreen-action-label">TNX</span>
  </button>
</div>





      </div>



      {showWalletPrompt && (
        <ActivateWalletPrompt
          threshold={Number(
            process.env.REACT_APP_MIN_MAINNET_ACTIVATION_PI || 0.1
          )}
          onClose={handleCloseWalletPrompt}
          onActivate={handleActivateWallet}
        />
      )}



{showQuickTopUp && (
  <QuickTopUpPopup
    open={showQuickTopUp}
    user={{ username: storedUser?.username }}
    minAmount={0.1}
    title="Insufficient Pi"
  
subtitle="Top up your BarterFi wallet to use post styles, boosts, and continue posting."
    onClose={() => setShowQuickTopUp(false)}
    onSuccess={() => {
      setShowQuickTopUp(false);
      showError('Wallet topped up successfully.', 2200);
    }}
  />
)}


      {showMentionDrawer && mentionResults.length > 0 && (
        <>
          {console.log('📢 Rendering MentionDrawer')}
          <MentionDrawer
            results={mentionResults}
            onSelect={handleMentionSelect}
            onClose={() => setShowMentionDrawer(false)}
          />
        </>
      )}


{fullscreenPreview && (
  <div
    className="createpostscreen-fullscreen-preview"
    onClick={() => setFullscreenPreview(null)}
  >
    <button
      type="button"
      className="createpostscreen-fullscreen-close"
      onClick={() => setFullscreenPreview(null)}
      aria-label="Close media preview"
    >
      ×
    </button>

    {fullscreenPreview.type === 'image' && (
      <img
        src={fullscreenPreview.url}
        alt="Fullscreen preview"
        className="createpostscreen-fullscreen-image"
        onClick={(e) => e.stopPropagation()}
      />
    )}

    {fullscreenPreview.type === 'video' && (
      <video
        src={fullscreenPreview.url}
        className="createpostscreen-fullscreen-video"
        controls
        autoPlay
        playsInline
        onClick={(e) => e.stopPropagation()}
      />
    )}
  </div>
)}

<PostStyleSelector
  open={styleDrawerOpen}
  selectedTheme={selectedBackgroundTheme}
  onSelectTheme={setSelectedBackgroundTheme}
  onClose={() => setStyleDrawerOpen(false)}
/>



<CreatePostBoostDrawer
  open={boostDrawerOpen}
  selectedBoost={selectedStakeBoost}
  onSelectBoost={setSelectedStakeBoost}
  onClearBoost={() => setSelectedStakeBoost(null)}
  onClose={() => setBoostDrawerOpen(false)}
/>

<PostActionSelector
  isOpen={postActionDrawerOpen}
  selectedAction={selectedPostAction}
  onSave={handleSavePostAction}
  onClear={handleClearPostAction}
  onClose={() => setPostActionDrawerOpen(false)}
/>

<PlanLimitDrawer
  open={planLimitOpen}
  onClose={() => setPlanLimitOpen(false)}
  limit={limitMeta.limit}
  planLabel={myPlan?.label || 'Free'}
  featureLabel={limitMeta.feature}
  unit={limitMeta.unit || 'characters'}
/>


    </div>
  );
}

export default CreatePostScreen;
